import React, { useState, useEffect } from 'react';

/**
 * Modal showing matching progress with countdown timer
 * Displays during the matching window after initiating a request
 * Can also be used for responders waiting for results
 */
function MatchingProgressModal({ isOpen, ticker, matchingWindow, startTime, onComplete, title, description }) {
    const [timeRemaining, setTimeRemaining] = useState(matchingWindow);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (!isOpen || !startTime) return;

        const interval = setInterval(() => {
            const elapsed = (Date.now() - startTime) / 1000;
            const remaining = Math.max(0, matchingWindow - elapsed);
            const progressPct = Math.min(100, (elapsed / matchingWindow) * 100);

            setTimeRemaining(remaining);
            setProgress(progressPct);

            if (remaining <= 0) {
                clearInterval(interval);
                if (onComplete) {
                    onComplete();
                }
            }
        }, 100);

        return () => clearInterval(interval);
    }, [isOpen, startTime, matchingWindow, onComplete]);

    if (!isOpen) return null;

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        if (mins > 0) {
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
        return `${secs}s`;
    };

    return (
        <div className="settings-modal-overlay">
            <div className="settings-modal" style={{ maxWidth: '450px' }}>
                <div className="settings-modal-header" style={{ padding: '12px 20px', textAlign: 'center' }}>
                    <h2 style={{ color: '#26a69a', margin: 0, fontSize: '1rem' }}>{title || 'Encrypted Matching In Progress'}</h2>
                </div>

                <div className="settings-modal-content" style={{ textAlign: 'center', padding: '15px 20px' }}>
                    <div style={{ marginBottom: '12px' }}>
                        <span style={{
                            fontSize: '1rem',
                            color: 'var(--text-secondary)',
                            textTransform: 'capitalize'
                        }}>
                            {ticker}
                        </span>
                    </div>

                    {/* Progress bar container */}
                    <div style={{
                        width: '100%',
                        height: '2px',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: '1px',
                        overflow: 'hidden',
                        marginBottom: '12px',
                        position: 'relative'
                    }}>
                        <div
                            className="progress-bar-fill"
                            style={{
                                width: `${progress}%`,
                                height: '100%',
                                borderRadius: '1px',
                                transition: 'width 0.1s linear',
                                background: 'linear-gradient(90deg, #26a69a 0%, #4db6ac 50%, #26a69a 100%)',
                                backgroundSize: '200% 100%',
                                boxShadow: '0 0 8px rgba(38, 166, 154, 0.6), 0 0 16px rgba(38, 166, 154, 0.3)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                className="progress-shimmer"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                                    animation: 'shimmer 1.5s infinite'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{
                        fontSize: '1.6rem',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        marginBottom: '6px'
                    }}>
                        {formatTime(timeRemaining)}
                    </div>

                    <div style={{
                        fontSize: '0.75rem',
                        color: '#888',
                        marginBottom: '12px'
                    }}>
                        {description || 'Collecting encrypted responses from liquidity providers'}
                    </div>

                    {/* Animated dots */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                style={{
                                    width: '5px',
                                    height: '5px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--accent-color)',
                                    animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 80%, 100% {
                        opacity: 0.3;
                        transform: scale(0.8);
                    }
                    40% {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                @keyframes shimmer {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(100%);
                    }
                }
            `}</style>
        </div>
    );
}

export default MatchingProgressModal;
